import { roles } from "constant/constants";
import PwaGuard from "helper/PwaGuard";
import Cycle from "pages/Cycle";
import SchoolSettings from "pages/SchoolSettings";
import SSOLogin from "pages/SSOLogin";
import SurveyForm from "pages/StudentDashboard/SurveyForm";
import { lazy } from "react";
import { FaFileExport, FaHome, FaUsers } from "react-icons/fa";
import {
  FaBookOpenReader,
  FaChartLine,
  FaClipboardList,
  FaCompass,
  FaFilePdf,
  FaHandshake,
  FaMagnifyingGlass,
  FaTableList,
  FaUser,
  FaUserGraduate,
  FaUsersGear,
} from "react-icons/fa6";
import { IoNotificationsSharp } from "react-icons/io5";
import { MdAssessment } from "react-icons/md";

export const permissions = {
  common: [roles.SCHOOL_ADMIN, roles.STUDENT, roles.SPECIALIST],
  schoolAdminAndSpecialist: [roles.SCHOOL_ADMIN, roles.SPECIALIST],
  schoolAdmin: [roles.SCHOOL_ADMIN],
  student: [roles.STUDENT],
};

/* ========================= Common Routes =================================== */

const Notifications = lazy(() => import("../pages/CommonPages/Notifications"));
const ProfileSettings = lazy(
  () => import("../pages/CommonPages/ProfileSettings"),
);
const Login = lazy(() => import("../pages/Login"));
const Signup = lazy(() => import("../pages/Signup"));

const Dashboard = lazy(() => import("../pages/CommonPages/Dashboard"));
const StudentManagement = lazy(
  () => import("../pages/CommonPages/StudentManagement"),
);
const StudentDetails = lazy(
  () => import("../pages/CommonPages/StudentManagement/StudentDetail"),
);

/* ========================= School Admin And Campus Manager Routes =================================== */

const AddEditStudent = lazy(
  () => import("../pages/SchoolAndCampus/AddEditStudent"),
);

const AddStudentCSV = lazy(
  () => import("../pages/SchoolAndCampus/AddStudentCSV"),
);

const AddEditRoster = lazy(
  () => import("../pages/SchoolAndCampus/AddEditRoster"),
);
const AddEditAssessmentCycle = lazy(
  () => import("../pages/SchoolAndCampus/TestSession/Create"),
);

const AdmissionsAssessment = lazy(
  () => import("../pages/SchoolAndCampus/AdmissionAssessment"),
);
const AddEditCandidate = lazy(() => import("../pages/AddEditCandidate"));
const AdmissionAssessmentDetails = lazy(
  () => import("../pages/SchoolAndCampus/AdmissionAssessmentDetails"),
);
const AdmissionAssessmentEvaluate = lazy(
  () => import("../pages/SchoolAndCampus/AdmissionAssessmentEvaluate"),
);
const MyEvaluations = lazy(
  () => import("../pages/SchoolAndCampus/MyEvaluations"),
);
const MyEvaluationEvaluate = lazy(
  () => import("../pages/SchoolAndCampus/MyEvaluationEvaluate"),
);
const Specialists = lazy(() => import("../pages/SchoolAdmin/Specialist"));
const SchoolAdmins = lazy(() => import("../pages/SchoolAdmins"));
const CampusManagement = lazy(() => import("../pages/CampusManagement"));
const CampusDetail = lazy(() => import("../pages/CampusManagement/Detail"));

const DataReview = lazy(() => import("../pages/SchoolAndCampus/DataReview"));

/* ========================= Only School Admin Routes =================================== */

const MyBundle = lazy(() => import("../pages/SchoolAdmin/MyBundle"));
const Bundles = lazy(() => import("../pages/Bundles"));
const AssessmentCycle = lazy(
  () => import("../pages/SchoolAdmin/AssessmentCycle"),
);
const AssessmentCycleDetails = lazy(
  () => import("../pages/SchoolAdmin/AssessmentCycle/AssessmentCycleDetails"),
);
const PromoteStudents = lazy(() => import("../pages/PromoteStudents"));
/* ========================= Only Student Routes =================================== */
const StartSession = lazy(
  () => import("../pages/StudentDashboard/StartSession"),
);
const Tests = lazy(() => import("../pages/StudentDashboard/Tests"));

const FcInsights = lazy(() => import("../pages/FcInsights"));
const ExportPage = lazy(() => import("../pages/Export"));
const FcRubrics = lazy(() => import("../pages/FcRubrics"));
const StudentReport = lazy(() => import("../pages/CommonPages/StudentReport"));

const MyReports = lazy(() => import("../pages/StudentDashboard/MyReports"));

export const routes = [
  {
    path: "/login",
    exact: true,
    protected: false,
    icon: <FaHome />,
    element: <Login />,
    showInSidebar: false,
  },
  {
    path: "/sso-login",
    exact: true,
    protected: false,
    icon: <FaHome />,
    element: <SSOLogin />,
    showInSidebar: false,
  },
  {
    path: "/signup",
    exact: true,
    protected: false,
    element: <Signup />,
    showInSidebar: false,
  },
  {
    path: "/",
    exact: true,
    protected: true,
    showInSidebar: true,
    title: "Dashboard",
    icon: <FaHome />,
    element: <Dashboard />,
    permission: permissions.common,
  },
  {
    path: "/group-assessment",
    exact: true,
    protected: true,
    showInSidebar: true,
    title: "Group Assessment",
    icon: <FaUsers />,
    element: <AssessmentCycle />,
    permission: permissions.schoolAdminAndSpecialist,
  },
  {
    path: "/group-assessment/:id",
    exact: true,
    protected: true,
    showInSidebar: false,
    element: <AssessmentCycleDetails />,
    permission: permissions.schoolAdminAndSpecialist,
  },
  {
    path: "/student/:slug",
    exact: true,
    protected: true,
    showInSidebar: false,
    element: <StudentDetails />,
    permission: permissions.schoolAdminAndSpecialist,
  },
  {
    path: "/student-management/add-student",
    exact: true,
    protected: true,
    showInSidebar: false,
    element: <AddEditStudent />,
    permission: permissions.schoolAdminAndSpecialist,
  },
  {
    path: "/student-management/add-students/csv",
    exact: true,
    protected: true,
    showInSidebar: false,
    element: <AddStudentCSV />,
    permission: permissions.schoolAdminAndSpecialist,
  },
  {
    path: "/student-management/add-roster",
    exact: true,
    protected: true,
    showInSidebar: false,
    element: <AddEditRoster />,
    permission: permissions.schoolAdminAndSpecialist,
  },
  {
    path: "/student-management/edit-roster/:id",
    exact: true,
    protected: true,
    showInSidebar: false,
    element: <AddEditRoster />,
    permission: permissions.schoolAdminAndSpecialist,
  },
  {
    path: "/add-edit-group-assessment",
    exact: true,
    protected: true,
    showInSidebar: false,
    element: <AddEditAssessmentCycle />,
    permission: permissions.schoolAdminAndSpecialist,
  },
  {
    path: "/quick-assessment",
    exact: true,
    protected: true,
    showInSidebar: true,
    title: "Quick Assessment",
    icon: <FaUser />,
    element: <AdmissionsAssessment />,
    permission: permissions.schoolAdminAndSpecialist,
  },
  {
    path: "/add-edit-candidate",
    exact: true,
    protected: true,
    showInSidebar: false,
    icon: <MdAssessment />,
    element: <AddEditCandidate />,
    permission: permissions.schoolAdminAndSpecialist,
  },
  {
    path: "/quick-assessment/:id",
    protected: true,
    showInSidebar: false,
    permission: permissions.schoolAdminAndSpecialist,
    element: <AdmissionAssessmentDetails />,
  },
  {
    path: "/quick-assessment-evaluate/:id",
    exact: true,
    protected: true,
    showInSidebar: false,
    element: <AdmissionAssessmentEvaluate />,
    permission: permissions.schoolAdminAndSpecialist,
  },
  {
    path: "/my-evaluations",
    exact: true,
    protected: true,
    showInSidebar: true,
    title: "My Evaluations",
    icon: <FaMagnifyingGlass />,
    element: <MyEvaluations />,
    permission: permissions.schoolAdminAndSpecialist,
  },
  {
    path: "/my-evaluation-evaluate/:id",
    exact: true,
    protected: true,
    showInSidebar: false,
    element: <MyEvaluationEvaluate />,
    permission: permissions.schoolAdminAndSpecialist,
  },
  {
    path: "/fc-insights",
    exact: true,
    protected: true,
    showInSidebar: true,
    title: "FC Insights",
    element: <FcInsights />,
    icon: <FaChartLine />,
    permission: permissions.schoolAdminAndSpecialist,
  },
  {
    path: "/export",
    exact: true,
    protected: true,
    showInSidebar: true,
    title: "Export",
    element: <ExportPage />,
    icon: <FaFileExport />,
    permission: permissions.schoolAdminAndSpecialist,
  },
  {
    path: "/campus-management/:id",
    exact: true,
    protected: true,
    showInSidebar: false,
    element: <CampusDetail />,
    permission: permissions.schoolAdmin,
  },
  {
    path: "",
    exact: true,
    protected: true,
    showInSidebar: true,
    title: "User Management",
    icon: <FaUsersGear />,
    subMenu: [
      {
        path: "/campus-management",
        exact: true,
        protected: true,
        showInSidebar: true,
        title: "Campus",
        icon: <FaUsersGear />,
        element: <CampusManagement />,
        permission: permissions.schoolAdmin,
      },
      {
        path: "/school-admins",
        exact: true,
        protected: true,
        showInSidebar: true,
        title: "School Admins",
        element: <SchoolAdmins />,
        permission: permissions.schoolAdmin,
      },
      {
        path: "/assessment-specialists",
        exact: true,
        protected: true,
        showInSidebar: true,
        title: "Assessment Specialists",
        element: <Specialists />,
        permission: permissions.schoolAdmin,
      },
      {
        path: "/student-management",
        exact: true,
        protected: true,
        showInSidebar: true,
        title: "Students",
        icon: <FaUsers />,
        element: <StudentManagement />,
        permission: permissions.schoolAdminAndSpecialist,
      },
    ],
  },
  {
    path: "/assessment-packages",
    exact: true,
    protected: true,
    element: <Bundles />,
    permission: permissions.schoolAdmin,
  },
  {
    path: "/my-package-credits",
    exact: true,
    protected: true,
    showInSidebar: true,
    title: "Credits & Billing",
    icon: <FaCompass />,
    element: <MyBundle />,
    permission: permissions.schoolAdmin,
  },
  {
    path: "/data-review",
    exact: true,
    protected: true,
    showInSidebar: true,
    title: "Data Review",
    icon: <FaHandshake />,
    element: <DataReview />,
    permission: permissions.schoolAdminAndSpecialist,
  },
  {
    path: "/fc-rubrics",
    exact: true,
    protected: true,
    showInSidebar: true,
    title: "FC Rubrics",
    element: <FcRubrics />,
    icon: <FaTableList />,
    permission: permissions.schoolAdminAndSpecialist,
  },
  {
    path: "/test-session/:type/:id/:sessionId",
    exact: true,
    protected: true,
    showInSidebar: false,
    title: "Test",
    element: (
      <PwaGuard>
        <StartSession />
      </PwaGuard>
    ),
    permission: permissions.student,
  },
  {
    path: "/tests",
    exact: true,
    protected: true,
    showInSidebar: true,
    title: "Tests",
    icon: <FaBookOpenReader />,
    element: <Tests />,
    permission: permissions.student,
  },
  {
    path: "/fc-report",
    exact: true,
    protected: true,
    showInSidebar: true,
    title: "FC Report",
    icon: <FaFilePdf />,
    element: <MyReports />,
    permission: permissions.student,
  },
  {
    path: "/survey-form",
    exact: true,
    protected: true,
    showInSidebar: true,
    title: "Student Survey",
    icon: <FaClipboardList />,
    element: <SurveyForm />,
    permission: permissions.student,
  },
  {
    path: "",
    exact: true,
    protected: true,
    showInSidebar: true,
    title: "School Settings",
    icon: <FaHome />,
    subMenu: [
      {
        path: "/school-settings",
        exact: true,
        protected: true,
        showInSidebar: true,
        title: "General",
        icon: <FaHome />,
        element: <SchoolSettings />,
        permission: permissions.schoolAdmin,
      },
      {
        path: "/cycle",
        exact: true,
        protected: true,
        showInSidebar: true,
        title: "Cycle",
        icon: <FaBookOpenReader />,
        element: <Cycle />,
        permission: permissions.schoolAdminAndSpecialist,
      },
      {
        path: "/promote",
        exact: true,
        protected: true,
        showInSidebar: true,
        title: "Promote",
        icon: <FaUserGraduate />,
        element: <PromoteStudents />,
        // moving the whole school up a grade is the school admin's call, the
        // same as the rest of School Settings
        permission: permissions.schoolAdmin,
      },
    ],
  },
  {
    path: "/notifications",
    exact: true,
    protected: true,
    showInSidebar: false,
    title: "Notifications",
    icon: <IoNotificationsSharp />,
    element: <Notifications />,
    permission: permissions.common,
  },
  {
    path: "/student-report/:id",
    exact: true,
    protected: false,
    showInSidebar: false,
    title: "Student Report",
    icon: <FaFilePdf />,
    element: <StudentReport />,
  },
  {
    path: "/profile-settings",
    exact: true,
    protected: true,
    showInSidebar: true,
    title: "Profile Settings",
    icon: <FaHome />,
    element: <ProfileSettings />,
    permission: permissions.common,
  },
];
